import {
  AreaResponse,
  IndustryLResponse,
  JobCountResponse,
  JobTypeLResponse,
} from '@doda/api-client/aspida/@types';
import { Dispatch, SetStateAction, useState } from 'react';
import {
  JobCountQuery,
  getJobCount,
} from '@doda/common/src/services/getJobCountAPI';
import { removeUndefinedFromObject } from '@doda/common/src/utils/objectUtil';
import { CompanyOfSearchModal } from '@doda/common/src/models/MasterCacheModel';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import { FeatureFlagSettings } from '@doda/common/src/constants/featureFlag';
import { useOccupation } from '../../conditions/Occupation/hooks/useOccupation';
import { useSettingConditionOnModal } from '../useSettingConditionOnModal';
import { useLocation } from '../../conditions/Location/hooks/useLocation';
import { useRouteStation } from '../../conditions/RouteStation/hooks/useRouteStaion';
import { useIndustry } from '../../conditions/Industry/hooks/useIndustry';
import { useEmploymentStatus } from '../../conditions/EmploymentStatus/hooks/useEmploymentStatus';
import { useSearchIncome } from '../../conditions/Income/hooks/useSearchIncome';
import { useSearchKeyword } from '../../conditions/keyword/hooks/useSearchKeyword';
import { useHolidayAndWayOfWorking } from '../../conditions/DetailCondition/hooks/useHolidayAndWayofWorking';
import { useRequirements } from '../../conditions/DetailCondition/hooks/useRequirements';
import { usePickUpCondition } from '../../conditions/PickUpCondition/hooks/usePickUpCondition';
import { useWorkplaceEnvironment } from '../../conditions/DetailCondition/hooks/useWorkplaceEnvironment';
import { useWelfareBenefits } from '../../conditions/DetailCondition/hooks/useWelfareBenefits';
import { useLanguageSkillList } from '../../conditions/DetailCondition/hooks/useLanguageSkills';
import { usePositions } from '../../conditions/DetailCondition/hooks/usePositions';
import { useEmployeeAverageAge } from '../../conditions/DetailCondition/hooks/useEmployeeAverageAge';
import { useEmployeeNum } from '../../conditions/DetailCondition/hooks/useEmployeeNum';
import { useEstablish } from '../../conditions/DetailCondition/hooks/useEstablish';
import { useJobOfferKind } from '../../conditions/DetailCondition/hooks/useJobOfferKind';
import { ValidationErrorType } from '../../interface';
import { InitialConditionsType } from '../../logics/createInitialCheckedList';

interface ArgumentType {
  initialJobCountData: JobCountResponse;
  masterData: {
    occupationMasterData: JobTypeLResponse[];
    areaMasterData: AreaResponse[];
    routeMasterData: CompanyOfSearchModal[];
    industryMasterData: IndustryLResponse[];
  };
  setValidationError: Dispatch<SetStateAction<ValidationErrorType>>;
  initialConditions: InitialConditionsType;
}

/**
 * 詳細条件に関するhooks
 * @param {object} param 詳細条件の生成用の引数
 * @param param.initialJobCountData 初期表示用のファセット件数
 * @param param.masterData マスタデータ
 * @param param.setValidationError エラー表示用のフラグ更新関数
 * @returns 詳細条件の値と更新する関数を含むオブジェクト
 */
export const useSearchCondition = ({
  initialJobCountData,
  masterData,
  setValidationError,
  initialConditions,
}: ArgumentType) => {
  const {
    setOccupationDisplayList,
    occupationDisplayList,
    selectedOccupationLIdList,
    selectedOccupationMIdList,
    selectedOccupationSIdList,
    updateOccupationFunctions,
    clearIsCheckOccupationList,
  } = useOccupation(initialConditions.occupationList);

  const {
    updateIsCheckedOccupationLList,
    updateIsCheckedOccupationMList,
    updateIsCheckedOccupationSList,
  } = updateOccupationFunctions;

  const {
    setLocationDisplayList,
    locationDisplayList,
    selectedAreaIdList,
    selectedPrefectureIdList,
    selectedCityIdList,
    selectedWardList,
    updateLocationFunctions,
    clearIsCheckLocationList,
  } = useLocation(initialConditions.locationList);

  const {
    updateIsCheckedAreaList,
    updateIsCheckedPrefectureList,
    updateIsCheckedCityList,
    updateIsCheckedWardList,
  } = updateLocationFunctions;

  const {
    setRouteStationDisplayList,
    routeStationDisplayList,
    selectedRouteIdList,
    selectedStationIdList,
    updateRouteStationFunctions,
    clearIsCheckRouteStationList,
  } = useRouteStation(initialConditions.routeStationList);

  const {
    setIndustryDisplayList,
    industryDisplayList,
    selectedIndustryLIdList,
    selectedIndustrySIdList,
    updateIndustryFunctions,
    clearIsCheckIndustryList,
  } = useIndustry(initialConditions.industryList);

  const { updateIsCheckedIndustryLList, updateIsCheckedIndustrySList } =
    updateIndustryFunctions;

  const {
    employmentStatusList,
    selectedEmploymentStatusIdList,
    updateIsCheckEmploymentStatusList,
    clearIsCheckEmploymentStatusList,
  } = useEmploymentStatus(initialConditions.employmentStatusList);

  const {
    upperIncome,
    lowerIncome,
    setLowerIncome,
    setUpperIncome,
    selectedLowerIncome,
    selectedUpperIncome,
    isIncomeRangeError,
    setIsIncomeRangeError,
    updateUpperIncome,
    updateLowerIncome,
    clearIsSelectedIncome,
  } = useSearchIncome(initialConditions.incomeValues);

  const { updateIsCheckedRouteList, updateIsCheckedStationList } =
    updateRouteStationFunctions;

  const [isAdobeTagFired, setIsAdobeTagFired] = useState(false);
  const includeSearchKeyword = useSearchKeyword(
    isAdobeTagFired,
    setIsAdobeTagFired,
    initialConditions.includeSearchKeyword.category,
    initialConditions.includeSearchKeyword.keyword
  );
  const excludeSearchKeyword = useSearchKeyword(
    isAdobeTagFired,
    setIsAdobeTagFired,
    initialConditions.excludedSearchKeyword.category,
    initialConditions.excludedSearchKeyword.keyword
  );

  const {
    holidayAndWayOfWorkingList,
    selectedHolidayAndWayOfWorkingList,
    updateHolidayAndWayOfWorkingListJobCount,
    updateIsCheckedHolidayAndWayOfWorkingList,
    clearIsCheckedHolidayAndWayOfWorkingList,
  } = useHolidayAndWayOfWorking(initialConditions.holidayAndWayOfWorkingList);

  const {
    requirementList,
    selectedRequirementList,
    updateIsCheckedRequirementList,
    updateRequirementListJobCount,
    clearIsCheckedRequirementList,
  } = useRequirements(initialConditions.requirementList);

  const {
    WorkplaceEnvironmentList,
    selectedWorkplaceEnvironmentList,
    updateIsCheckedWorkplaceEnvironmentList,
    updateWorkplaceEnvironmentListJobCount,
    clearIsCheckedWorkplaceEnvironmentList,
  } = useWorkplaceEnvironment(initialConditions.workplaceEnvironmentList);

  const {
    welfareBenefitsList,
    selectedWelfareBenefitsList,
    updateIsCheckedWelfareBenefitsList,
    updateWelfareBenefitsListJobCount,
    clearIsCheckedWelfareBenefitsList,
  } = useWelfareBenefits(initialConditions.welfareBenefitsList);

  const {
    languageSkillList,
    selectedLanguageSkillList,
    updateIsCheckedLanguageSkillList,
    updateLanguageListJobCount,
    clearIsCheckedLanguageList,
  } = useLanguageSkillList(initialConditions.languageSkillList);

  const {
    positionList,
    selectedPositionList,
    updateIsCheckedPositionList,
    updatePositionListJobCount,
    clearIsCheckedPositionList,
  } = usePositions(initialConditions.positionList);

  const {
    employeeAverageAgeList,
    selectedEmployeeAverageAgeList,
    updateIsCheckedEmployeeAverageAgeList,
    updateEmployeeAverageAgeListJobCount,
    clearIsCheckedEmployeeAverageAgeList,
  } = useEmployeeAverageAge(initialConditions.employeeAverageAgeData);

  const {
    employNumList,
    selectedEmployNumList,
    updateIsCheckedEmployNumList,
    updateEmployNumListJobCount,
    clearIsCheckedEmployNumList,
  } = useEmployeeNum(initialConditions.employNumList);

  const {
    establishList,
    selectedEstablishList,
    updateIsCheckedEstablishList,
    updateEstablishListJobCount,
    clearIsCheckedEstablishList,
  } = useEstablish(initialConditions.establishList);

  const {
    jobOfferKindList,
    selectedJobOfferKindList,
    updateIsCheckedJobOfferKindList,
    updateJobOfferKindListJobCount,
    clearIsCheckedJobOfferKindList,
  } = useJobOfferKind(initialConditions.jobOfferKindList);

  const {
    pickUpConditionDisplayList,
    isNewArrival,
    isNearDeadline,
    updateIsCheckedIsNewArrival,
    updateIsCheckedIsNearDeadline,
    clearIsCheckedIsNewArrivalAndIsNearDeadline,
  } = usePickUpCondition({
    initialIsNewArrival: initialConditions.isNewArrival,
    initialIsNearDeadLine: initialConditions.isNearDeadline,
    holidayAndWayOfWorkingList,
    requirementList,
  });

  const [jobCountData, setJobCountData] =
    useState<JobCountResponse>(initialJobCountData);
  const [modalTotalCount, setModalTotalCount] = useState(0);

  const useV2JobCountApi = useFeatureFlag(
    FeatureFlagSettings.JOB_SEARCH_LIST_PC
  );

  const selectedCurrentCondition: SelectedCurrentConditionType = {
    searchJobTypeLId: selectedOccupationLIdList,
    searchJobTypeMId: selectedOccupationMIdList,
    searchJobTypeSId: selectedOccupationSIdList,
    industryLId: selectedIndustryLIdList,
    industrySId: selectedIndustrySIdList,
    areaId: selectedAreaIdList,
    prefectureId: selectedPrefectureIdList,
    cityId: selectedCityIdList,
    wardId: selectedWardList,
    routeId: selectedRouteIdList,
    stationId: selectedStationIdList,
    employmentStatus: selectedEmploymentStatusIdList,
    isNewArrival,
    isNearDeadline,
    holidayAndWayOfWorking: selectedHolidayAndWayOfWorkingList,
    requirements: selectedRequirementList,
    lowerLimitIncome: selectedLowerIncome,
    upperLimitIncome: selectedUpperIncome,
    includedKeywordCategory: includeSearchKeyword.state.searchText
      ? includeSearchKeyword.state.searchCategory
      : undefined,
    includedKeyword: includeSearchKeyword.state.searchText
      ? includeSearchKeyword.state.searchText.split(/\s+/)
      : [],
    excludedKeywordCategory: excludeSearchKeyword.state.searchText
      ? excludeSearchKeyword.state.searchCategory
      : undefined,
    excludedKeyword: excludeSearchKeyword.state.searchText
      ? excludeSearchKeyword.state.searchText.split(/\s+/)
      : [],
    workplaceEnvironment: selectedWorkplaceEnvironmentList,
    welfareBenefits: selectedWelfareBenefitsList,
    languageSkills: selectedLanguageSkillList,
    positions: selectedPositionList,
    employeeAverageAge: selectedEmployeeAverageAgeList,
    employNum: selectedEmployNumList,
    establish: selectedEstablishList,
    jobOfferKind: selectedJobOfferKindList,
  };

  const setUpdatedJobCount = (jobCountParams: JobCountResponse) => {
    updateHolidayAndWayOfWorkingListJobCount(
      jobCountParams.holidayAndWayOfWorkingList
    );
    updateRequirementListJobCount(jobCountParams.requirementsList);
    updateWorkplaceEnvironmentListJobCount(
      jobCountParams.workplaceEnvironmentList
    );
    updateWelfareBenefitsListJobCount(jobCountParams.welfareBenefitsList);
    updateLanguageListJobCount(jobCountParams.languageSkillsList);
    updatePositionListJobCount(jobCountParams.positionsList);
    updateEmployeeAverageAgeListJobCount(jobCountParams.employeeAverageAgeList);
    updateEmployNumListJobCount(jobCountParams.employNumList);
    updateEstablishListJobCount(jobCountParams.establishList);
    updateJobOfferKindListJobCount(jobCountParams.jobOfferKindList);
  };

  const updateJobCount = async (currentCondition?: JobCountQuery) => {
    const conditions = {
      ...selectedCurrentCondition,
      ...currentCondition,
    };
    const res = await getJobCount(
      useV2JobCountApi,
      removeUndefinedFromObject(conditions)
    );
    setJobCountData(res);
    setUpdatedJobCount(res);
  };

  const allClearSelectedCondition = async () => {
    clearIsCheckOccupationList();
    clearIsCheckLocationList();
    clearIsCheckRouteStationList();
    clearIsCheckIndustryList();
    clearIsCheckEmploymentStatusList();
    clearIsSelectedIncome();
    includeSearchKeyword.updateFunctions.clearIsSelectedKeyword();
    excludeSearchKeyword.updateFunctions.clearIsSelectedKeyword();
    clearIsCheckedHolidayAndWayOfWorkingList();
    clearIsCheckedRequirementList();
    clearIsCheckedIsNewArrivalAndIsNearDeadline();
    clearIsCheckedWorkplaceEnvironmentList();
    clearIsCheckedWelfareBenefitsList();
    clearIsCheckedLanguageList();
    clearIsCheckedPositionList();
    clearIsCheckedEmployeeAverageAgeList();
    clearIsCheckedEmployNumList();
    clearIsCheckedEstablishList();
    clearIsCheckedJobOfferKindList();

    const res = await getJobCount(useV2JobCountApi);
    setJobCountData(res);
    setUpdatedJobCount(res);

    setValidationError({
      isErrorNonCondition: false,
      isErrorConditionUpperLimit: false,
    });
  };

  /**
   * モーダルに渡す件数更新処理
   * @param currentCondition
   */
  const updateModalJobCount = async (currentCondition?: JobCountQuery) => {
    const conditions = {
      ...selectedCurrentCondition,
      ...currentCondition,
    };

    const res = await getJobCount(
      useV2JobCountApi,
      removeUndefinedFromObject(conditions)
    );

    setModalTotalCount(res.totalCount ?? 0);
  };

  const {
    onClickOccupationModalSettingButton,
    onClickLocationModalSettingButton,
    onClickRouteStationModalSettingButton,
    onClickIndustryModalSettingButton,
  } = useSettingConditionOnModal({
    selectedCurrentCondition,
    setOccupationDisplayList,
    setLocationDisplayList,
    setRouteStationDisplayList,
    setIndustryDisplayList,
    updateJobCount,
    masterData,
  });

  return {
    occupation: {
      occupationDisplayList,
      updateIsCheckedOccupationLList,
      updateIsCheckedOccupationMList,
      updateIsCheckedOccupationSList,
    },
    location: {
      locationDisplayList,
      updateIsCheckedAreaList,
      updateIsCheckedPrefectureList,
      updateIsCheckedCityList,
      updateIsCheckedWardList,
    },
    routeStation: {
      routeStationDisplayList,
      updateIsCheckedRouteList,
      updateIsCheckedStationList,
    },
    industry: {
      industryDisplayList,
      updateIsCheckedIndustryLList,
      updateIsCheckedIndustrySList,
    },
    employmentStatus: {
      employmentStatusList,
      updateIsCheckEmploymentStatusList,
    },
    income: {
      upperIncome,
      lowerIncome,
      setLowerIncome,
      setUpperIncome,
      isIncomeRangeError,
      setIsIncomeRangeError,
      updateUpperIncome,
      updateLowerIncome,
    },
    pickUpCondition: {
      pickUpConditionDisplayList,
      updateIsCheckedIsNewArrival,
      updateIsCheckedIsNearDeadline,
    },
    searchDetail: {
      holidayAndWayOfWorking: {
        holidayAndWayOfWorkingList,
        updateIsCheckedHolidayAndWayOfWorkingList,
      },
      requirements: {
        requirementList,
        updateIsCheckedRequirementList,
      },
      workplaceEnvironment: {
        WorkplaceEnvironmentList,
        updateIsCheckedWorkplaceEnvironmentList,
      },
      welfareBenefits: {
        welfareBenefitsList,
        updateIsCheckedWelfareBenefitsList,
      },
      languageSkill: {
        languageSkillList,
        updateIsCheckedLanguageSkillList,
      },
      positions: {
        positionList,
        updateIsCheckedPositionList,
      },
      employeeAverageAge: {
        employeeAverageAgeList,
        updateIsCheckedEmployeeAverageAgeList,
      },
      employNum: {
        employNumList,
        updateIsCheckedEmployNumList,
      },
      establish: {
        establishList,
        updateIsCheckedEstablishList,
      },
      jobOfferKind: {
        jobOfferKindList,
        updateIsCheckedJobOfferKindList,
      },
    },
    includeSearchKeyword,
    excludeSearchKeyword,
    selectedCurrentCondition,
    updateJobCount,
    jobCountData,
    updateModalJobCount,
    modalTotalCount,
    allClearSelectedCondition,
    onClickOccupationModalSettingButton,
    onClickLocationModalSettingButton,
    onClickRouteStationModalSettingButton,
    onClickIndustryModalSettingButton,
  };
};
